import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { awards, awardsCount } from "@/data/awards";

/**
 * Normalise the various shapes a single award may carry into an array of
 * { src, caption } objects. Supports the new `images: []` array as well as the
 * legacy `image` / `image2` fields.
 */
function getImages(a) {
  if (Array.isArray(a.images) && a.images.length > 0) {
    return a.images.map((img) =>
      typeof img === "string" ? { src: img, caption: "" } : img,
    );
  }
  const legacy = [];
  if (a.image) legacy.push({ src: a.image, caption: a.imageAlt || "" });
  if (a.image2) legacy.push({ src: a.image2, caption: "" });
  return legacy;
}

function HeroAwards() {
  const { t } = useTranslation();
  return (
    <section className="relative pt-32 pb-12 overflow-hidden bg-brand-dark text-white">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(144,184,73,0.22),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(251,191,36,0.14),transparent_60%)]" />
        <div className="absolute inset-0 tech-grid-dark opacity-40" />
      </div>

      {/* Floating laurel motifs */}
      <div aria-hidden className="absolute inset-0 z-0 pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: [0, 0.35, 0.2], y: [0, -20, 0] }}
            transition={{ duration: 8 + i * 0.6, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
            style={{ left: `${(i * 13 + 8) % 92}%`, top: `${(i * 19 + 25) % 70}%` }}
            className="absolute text-brand/40 text-2xl material-symbols-outlined"
          >
            emoji_events
          </motion.span>
        ))}
      </div>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-3 gap-md items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand/30 bg-brand/10 text-brand text-[11px] font-bold uppercase tracking-widest mb-6">
              <span className="material-symbols-outlined icon-fill text-[14px]">emoji_events</span>
              {t("awards.hero.eyebrow")}
            </span>
            <h1 className="font-h1 text-[40px] md:text-[56px] leading-[1.05] font-extrabold mb-4">
              {t("awards.hero.title_a")}{" "}
              <span className="bg-gradient-to-r from-brand via-emerald-300 to-teal-300 bg-clip-text text-transparent">
                {t("awards.hero.title_accent")}
              </span>
            </h1>
            <p className="text-lg text-zinc-300 max-w-2xl">{t("awards.hero.intro")}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4"
          >
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
              {t("awards.hero.count_label")}
            </div>
            <div className="font-manrope font-extrabold text-3xl text-brand">{awardsCount}</div>
            <div className="mt-2 text-[10px] uppercase tracking-widest text-zinc-500">
              {t("awards.hero.more_soon")}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function AwardImageGallery({ images, accent, year, featured, t }) {
  const [active, setActive] = useState(0);
  const total = images.length;

  useEffect(() => {
    if (total <= 1) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % total), 6500);
    return () => clearInterval(id);
  }, [total]);

  const current = images[active];

  return (
    <div className="relative flex flex-col h-full bg-zinc-950">
      {/* Main image */}
      <div className="relative flex-1 min-h-[380px] lg:min-h-[520px] overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.img
            key={current.src}
            src={current.src}
            alt={current.caption || `Award image ${active + 1}`}
            loading="lazy"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </AnimatePresence>

        {/* soft vignette so overlaid pills stay legible */}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 via-transparent to-zinc-950/30 pointer-events-none" />

        {/* top pills */}
        <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur border border-white/40 text-[10px] uppercase tracking-widest text-primary font-bold z-10">
          <span className="material-symbols-outlined icon-fill text-brand text-[14px]">emoji_events</span>
          {year}
        </span>
        {featured && (
          <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-950/85 backdrop-blur border border-brand/40 text-[10px] uppercase tracking-widest text-brand font-bold z-10">
            {t("awards.badge.featured")}
          </span>
        )}

        {/* caption bar */}
        {current.caption && (
          <div className="absolute bottom-0 inset-x-0 z-10 px-4 py-3 flex items-center gap-2">
            <span className={`inline-block h-2 w-2 rounded-full bg-gradient-to-r ${accent}`} />
            <span className="text-[11px] uppercase tracking-widest text-white/90 font-mono truncate">
              {current.caption}
            </span>
          </div>
        )}

        {/* prev / next arrows */}
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={() => setActive((i) => (i - 1 + total) % total)}
              data-testid="award-gallery-prev"
              aria-label="Previous image"
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 h-9 w-9 grid place-items-center rounded-full bg-zinc-950/70 hover:bg-zinc-950/90 backdrop-blur border border-white/20 text-white transition-all hover:scale-105"
            >
              <span className="material-symbols-outlined text-[20px]">chevron_left</span>
            </button>
            <button
              type="button"
              onClick={() => setActive((i) => (i + 1) % total)}
              data-testid="award-gallery-next"
              aria-label="Next image"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 h-9 w-9 grid place-items-center rounded-full bg-zinc-950/70 hover:bg-zinc-950/90 backdrop-blur border border-white/20 text-white transition-all hover:scale-105"
            >
              <span className="material-symbols-outlined text-[20px]">chevron_right</span>
            </button>

            {/* index counter */}
            <span className="absolute bottom-3 right-3 z-20 font-mono text-[10px] tracking-widest text-white/90 bg-zinc-950/70 backdrop-blur px-2 py-0.5 rounded-full border border-white/20">
              {String(active + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {total > 1 && (
        <div className="relative border-t border-brand/25 bg-zinc-950 px-3 py-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {images.map((img, idx) => (
              <button
                type="button"
                key={img.src}
                onClick={() => setActive(idx)}
                data-testid={`award-gallery-thumb-${idx}`}
                aria-label={`View image ${idx + 1}`}
                className={`relative flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                  idx === active
                    ? "border-brand shadow-[0_0_0_2px_rgba(144,184,73,0.35)]"
                    : "border-white/10 hover:border-white/40 opacity-70 hover:opacity-100"
                }`}
              >
                <img
                  src={img.src}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                {idx === active && (
                  <div className="absolute inset-0 ring-2 ring-inset ring-brand/60 pointer-events-none" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FeaturedAward({ a }) {
  const { t } = useTranslation();
  const images = getImages(a);
  const hasImages = images.length > 0;
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`award-featured-${a.slug}`}
      className="group relative bg-white rounded-3xl border border-outline-variant overflow-hidden hover:shadow-2xl transition-shadow"
    >
      <div className={`h-1.5 bg-gradient-to-r ${a.accent}`} />
      <div className="grid lg:grid-cols-5 gap-0">
        {/* Left: gallery or plaque */}
        <div className={`lg:col-span-2 relative flex flex-col overflow-hidden ${hasImages ? "bg-zinc-950" : `bg-gradient-to-br ${a.accent}`}`}>
          {hasImages ? (
            <AwardImageGallery
              images={images}
              accent={a.accent}
              year={a.year}
              featured
              t={t}
            />
          ) : (
            <div className="relative aspect-[3/4] lg:aspect-auto lg:min-h-[420px] flex flex-col items-center justify-center text-white p-md text-center">
              <div className="absolute inset-0 tech-grid-dark opacity-20" />
              <div className="absolute inset-4 border border-white/30 rounded-2xl pointer-events-none" />
              <div className="absolute inset-8 border border-white/15 rounded-xl pointer-events-none" />
              <div className="relative z-10 flex flex-col items-center max-w-[80%]">
                <div className="flex items-center gap-2 opacity-90 mb-4">
                  <span className="material-symbols-outlined icon-fill text-white text-[32px] -scale-x-100">military_tech</span>
                  <span className="material-symbols-outlined icon-fill text-white text-[60px]">emoji_events</span>
                  <span className="material-symbols-outlined icon-fill text-white text-[32px]">military_tech</span>
                </div>
                <div className="font-mono text-[11px] uppercase tracking-[0.35em] text-white/85 mb-3">
                  Awarded {a.year}
                </div>
                <div className="font-manrope font-black text-white text-2xl leading-tight drop-shadow mb-3">
                  {a.awardName}
                </div>
                {a.location && (
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/80 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">location_on</span>
                    {a.location}
                  </div>
                )}
                <div className="mt-4 pt-4 border-t border-white/25 font-mono text-[10px] uppercase tracking-widest text-white/85">
                  {a.recipient}
                </div>
              </div>
              <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur border border-white/40 text-[10px] uppercase tracking-widest text-primary font-bold">
                <span className="material-symbols-outlined icon-fill text-brand text-[14px]">emoji_events</span>
                {a.year}
              </span>
              <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-950/85 backdrop-blur border border-brand/40 text-[10px] uppercase tracking-widest text-brand font-bold">
                {t("awards.badge.featured")}
              </span>
            </div>
          )}
        </div>

        {/* Right: content */}
        <div className="lg:col-span-3 p-md md:p-lg flex flex-col justify-center">
          <div className="text-xs uppercase tracking-widest text-brand font-bold mb-2 flex items-center gap-2 flex-wrap">
            <span>{a.awardedBy}</span>
            {a.publisher && (
              <>
                <span className="w-1 h-1 rounded-full bg-brand" />
                <span className="text-zinc-500 normal-case tracking-normal font-normal">{a.publisher}</span>
              </>
            )}
          </div>
          <h3 className="font-h2 text-3xl md:text-4xl leading-tight text-primary mb-3">
            {a.awardName}
          </h3>
          <div className="inline-block self-start px-3 py-1.5 rounded-full bg-tertiary-fixed text-brand text-xs font-bold uppercase tracking-widest mb-5">
            {a.category}
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
                {t("awards.card.recipient")}
              </div>
              <div className="font-manrope font-bold text-primary text-lg leading-tight">
                {a.recipient}
              </div>
              <div className="text-sm text-on-surface-variant mt-0.5">{a.recipientRole}</div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
                {t("awards.card.for")}
              </div>
              <div className="text-sm text-primary font-medium">{a.project}</div>
            </div>

            {a.location && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
                  {t("awards.card.venue")}
                </div>
                <div className="text-sm text-primary font-medium flex items-center gap-1">
                  <span className="material-symbols-outlined text-brand text-[16px]">location_on</span>
                  {a.location}
                </div>
              </div>
            )}
          </div>

          {a.citation && (
            <blockquote className="relative pl-4 border-l-2 border-brand text-on-surface-variant italic text-sm md:text-base">
              &ldquo;{a.citation}&rdquo;
            </blockquote>
          )}

          {a.hashtag && (
            <div className="mt-5 inline-flex items-center gap-2">
              <span className="text-xs text-zinc-500">{a.hashtag}</span>
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
}

function StandardAwardCard({ a, i }) {
  const { t } = useTranslation();
  const images = getImages(a);
  const cover = images[0]?.src || null;
  return (
    <motion.article
      key={a.slug}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: i * 0.06 }}
      data-testid={`award-card-${a.slug}`}
      className="group bg-white rounded-2xl border border-outline-variant overflow-hidden hover:border-brand hover:shadow-xl transition-all flex flex-col"
    >
      <div className={`h-1.5 bg-gradient-to-r ${a.accent}`} />
      <div className={`relative aspect-[3/4] overflow-hidden ${cover ? "bg-zinc-100" : `bg-gradient-to-br ${a.accent}`}`}>
        {cover ? (
          <img
            src={cover}
            alt={a.awardName}
            loading="lazy"
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-md text-center">
            <div className="absolute inset-0 tech-grid-dark opacity-25" />
            <div className="absolute inset-3 border border-white/30 rounded-2xl pointer-events-none" />
            <div className="absolute inset-6 border border-white/15 rounded-xl pointer-events-none" />
            <div className="relative z-10 flex flex-col items-center">
              <div className="flex items-center gap-2 opacity-90 mb-3">
                <span className="material-symbols-outlined icon-fill text-white text-[26px] -scale-x-100">military_tech</span>
                <span className="material-symbols-outlined icon-fill text-white text-[46px]">emoji_events</span>
                <span className="material-symbols-outlined icon-fill text-white text-[26px]">military_tech</span>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/80 mb-2">
                Awarded {a.year}
              </div>
              <div className="font-manrope font-black text-white text-lg leading-tight drop-shadow">
                {a.awardName}
              </div>
              <div className="mt-3 pt-3 border-t border-white/25 font-mono text-[10px] uppercase tracking-widest text-white/80">
                {a.recipient}
              </div>
            </div>
          </div>
        )}
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] uppercase tracking-widest text-primary font-bold">
          <span className="material-symbols-outlined icon-fill text-brand text-[14px]">emoji_events</span>
          {a.year}
        </span>
      </div>
      <div className="p-md flex flex-col flex-1">
        <div className="text-xs uppercase tracking-widest text-brand font-bold mb-1">{a.awardedBy}</div>
        <h3 className="font-h3 text-xl text-primary leading-tight mb-2">{a.awardName}</h3>
        <div className="text-xs text-on-surface-variant mb-3">{a.category}</div>
        <div className="text-sm font-bold text-primary">{a.recipient}</div>
        <div className="text-xs text-on-surface-variant">{t("awards.card.for")}: {a.project}</div>
      </div>
    </motion.article>
  );
}

function AwardsList() {
  const { t } = useTranslation();
  const featured = awards.filter((a) => a.featured);
  const rest = awards.filter((a) => !a.featured);

  return (
    <section className="max-w-[1280px] mx-auto px-6 py-xl">
      {featured.length > 0 && (
        <div className="mb-lg">
          <span className="text-brand text-label-caps">{t("awards.section.featured_eyebrow")}</span>
          <h2 className="font-h2 text-h2 text-primary mt-1 mb-md">{t("awards.section.featured_title_multi")}</h2>
          <div className="space-y-md">
            {featured.map((a) => (
              <FeaturedAward key={a.slug} a={a} />
            ))}
          </div>
        </div>
      )}

      {rest.length > 0 && (
        <div className="mt-xl">
          <span className="text-brand text-label-caps">{t("awards.section.more_eyebrow")}</span>
          <h2 className="font-h2 text-h2 text-primary mt-1 mb-md">{t("awards.section.more_title")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-gutter">
            {rest.map((a, i) => (
              <StandardAwardCard key={a.slug} a={a} i={i} />
            ))}
          </div>
        </div>
      )}

      {/* Coming-soon placeholder — signals momentum */}
      <div className="mt-xl grid md:grid-cols-3 gap-gutter">
        {[0, 1, 2].map((n) => (
          <motion.div
            key={n}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: n * 0.08 }}
            className="relative rounded-2xl border-2 border-dashed border-outline-variant p-md text-center bg-white/40 backdrop-blur"
          >
            <span className="material-symbols-outlined text-brand/60 text-4xl mb-3 inline-block">workspace_premium</span>
            <div className="font-manrope font-bold text-primary/70">{t("awards.upcoming.title")}</div>
            <p className="text-xs text-on-surface-variant mt-1">{t("awards.upcoming.desc")}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Cta() {
  const { t } = useTranslation();
  return (
    <section className="bg-primary-container text-white py-xl relative overflow-hidden">
      <div className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-brand/15 blur-3xl" />
      <div className="max-w-[1280px] mx-auto px-6 relative z-10 grid md:grid-cols-2 gap-md items-center">
        <div>
          <span className="text-brand text-label-caps">{t("awards.cta.eyebrow")}</span>
          <h2 className="font-h2 text-h2 mt-2 mb-3">{t("awards.cta.title")}</h2>
          <p className="text-zinc-300 max-w-xl">{t("awards.cta.intro")}</p>
        </div>
        <div className="md:text-right">
          <a
            href="mailto:press@africaocems.com"
            data-testid="awards-press-contact"
            className="inline-flex items-center gap-2 px-7 py-4 bg-brand text-brand-dark font-bold rounded-xl shadow-lg hover:-translate-y-0.5 transition-all"
          >
            press@africaocems.com
            <span className="material-symbols-outlined">arrow_forward</span>
          </a>
        </div>
      </div>
    </section>
  );
}

export default function Awards() {
  return (
    <>
      <HeroAwards />
      <AwardsList />
      <Cta />
    </>
  );
}
